import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Phone, Clock, Shield, CheckCircle, Key, Zap, Droplet, Wind, Square } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import StickyCallButton from '@/components/StickyCallButton';
import PageSEO from '@/components/PageSEO';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CityPage = () => {
  const { citySlug } = useParams();
  const [city, setCity] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCity = async () => {
      try {
        const response = await axios.get(`${API}/cities/${citySlug}`);
        setCity(response.data);
      } catch (error) {
        console.error('Fehler beim Laden der Stadt:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCity();
  }, [citySlug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex items-center justify-center py-20">
          <p className="text-gray-500">Lade Daten...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!city) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex flex-col items-center justify-center py-20">
          <p className="text-gray-500 text-lg mb-4">Stadt nicht gefunden</p>
          <Link to="/einsatzgebiete" className="text-[#0066cc] hover:underline">Zurück zur Übersicht</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const faqs = [
    {
      q: `Wie schnell sind Sie in ${city.name}?`,
      a: `Unser Notdienst bemüht sich, schnellstmöglich in ${city.name} vor Ort zu sein. Je nach Stadtteil und Verkehrslage erreichen wir Sie in der Regel innerhalb von 30-60 Minuten.`,
    },
    {
      q: `Welche Stadtteile in ${city.name} decken Sie ab?`,
      a: `Wir sind in allen Stadtteilen von ${city.name} für Sie im Einsatz. Egal ob ${city.districts?.slice(0, 3).map(d => d.name).join(', ')} oder andere Bezirke – wir kommen zu Ihnen.`,
    },
    {
      q: 'Welche Notdienste bieten Sie an?',
      a: 'Wir bieten Schlüsseldienst, Elektro-Notdienst, Sanitär- und Heizungsnotdienst, Rohrreinigung, Glas-Notdienst sowie Einbruchschutz an.',
    },
    {
      q: 'Sind Sie auch nachts und am Wochenende erreichbar?',
      a: 'Ja, unser Notdienst ist 24 Stunden am Tag, 7 Tage die Woche erreichbar – auch nachts, an Wochenenden und Feiertagen.',
    },
  ];

  // JSON-LD Schema
  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": `Handwerkernotdienst NRW in ${city.name}`,
    "telephone": "+49-231-39083687",
    "email": "info@handwerkernotdienst.nrw",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": city.name,
      "addressRegion": "Nordrhein-Westfalen",
      "addressCountry": "DE"
    },
    "openingHoursSpecification": [{
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      "opens": "00:00",
      "closes": "23:59"
    }],
    "areaServed": city.name
  };

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <PageSEO
        title={`24/7 Handwerkernotdienst in ${city.name} | Schnelle Hilfe vor Ort`}
        description={`Handwerkernotdienst in ${city.name}: Schlüsseldienst, Elektro-Notdienst, Sanitär, Rohrreinigung & mehr. 24/7 erreichbar ☎ 0231 39083687. Schnell vor Ort in allen Stadtteilen.`}
        canonicalUrl={`https://handwerkernotdienst.nrw/einsatzgebiete/${city.slug}`}
        breadcrumbs={[
          { name: 'Start', url: 'https://handwerkernotdienst.nrw/' },
          { name: 'Einsatzgebiete', url: 'https://handwerkernotdienst.nrw/einsatzgebiete' },
          { name: city.name, url: `https://handwerkernotdienst.nrw/einsatzgebiete/${city.slug}` }
        ]}
        schema={schema}
      />
      
      <Header />
      <StickyCallButton />

      {/* JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#e8f4f8] to-white py-16" data-testid="city-hero">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-2 text-sm text-gray-500 mb-6">
            <Link to="/" className="hover:text-[#0066cc]">Start</Link>
            <span>/</span>
            <Link to="/einsatzgebiete" className="hover:text-[#0066cc]">Einsatzgebiete</Link>
            <span>/</span>
            <span className="text-gray-900">{city.name}</span>
          </div>

          <div className="flex items-start space-x-4 mb-6">
            <div className="w-16 h-16 bg-[#0066cc] rounded-xl flex items-center justify-center flex-shrink-0">
              <MapPin className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-3" data-testid="city-title">
                24/7 Handwerkernotdienst in {city.name}
              </h1>
              <p className="text-lg text-gray-600">
                Schnelle Hilfe rund um die Uhr – Ihr zuverlässiger Notdienst vor Ort
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <a href="tel:+4923139083687" data-testid="city-call-button">
              <Button size="lg" className="bg-[#e63946] hover:bg-[#d62828] text-white px-8 rounded-full font-bold">
                <Phone className="w-5 h-5 mr-2" />
                Jetzt anrufen: 0231 39083687
              </Button>
            </a>
            <Link to="/kontakt" data-testid="city-contact-button">
              <Button size="lg" variant="outline" className="border-2 border-[#0066cc] text-[#0066cc] hover:bg-[#0066cc] hover:text-white rounded-full font-semibold">
                Kontaktformular
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-12 bg-white" data-testid="city-features">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-gray-50 rounded-xl">
              <Clock className="w-10 h-10 text-[#0066cc] mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900 mb-2">24/7 Verfügbar</h3>
              <p className="text-gray-600 text-sm">Rund um die Uhr für Sie da</p>
            </div>
            <div className="text-center p-6 bg-gray-50 rounded-xl">
              <MapPin className="w-10 h-10 text-[#0066cc] mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900 mb-2">Schnell vor Ort</h3>
              <p className="text-gray-600 text-sm">Kurze Anfahrtszeiten in {city.name}</p>
            </div>
            <div className="text-center p-6 bg-gray-50 rounded-xl">
              <Shield className="w-10 h-10 text-[#0066cc] mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900 mb-2">Zuverlässig</h3>
              <p className="text-gray-600 text-sm">Professionelle Fachkräfte</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16" data-testid="city-content">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Ihr Handwerkernotdienst in {city.name}</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              {city.description} Mit {city.population ? `rund ${city.population} Einwohnern` : 'seiner Größe'} gehört {city.name} zu den wichtigen Städten in Nordrhein-Westfalen. 
              Unser Handwerkernotdienst ist in der gesamten Stadt und allen Stadtteilen für Sie im Einsatz.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              Ob Ausgesperrt in der Innenstadt, Wasserschaden im Wohngebiet oder Stromausfall im Gewerbegebiet – 
              wir sind schnellstmöglich vor Ort und lösen Ihr Problem professionell. Unsere erfahrenen Techniker kennen {city.name} und die Gegebenheiten vor Ort.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Unsere Leistungen in {city.name}</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              <Link to={`/einsatzgebiete/${city.slug}/schluesseldienst`} className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200">
                <Key className="w-6 h-6 text-[#0066cc] flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Schlüsseldienst</h4>
                  <p className="text-gray-600 text-sm">Türöffnung ohne Schäden</p>
                  <span className="text-[#0066cc] text-sm font-semibold mt-1 inline-block">Mehr erfahren →</span>
                </div>
              </Link>
              <Link to={`/einsatzgebiete/${city.slug}/elektro-notdienst`} className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200">
                <Zap className="w-6 h-6 text-[#0066cc] flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Elektro-Notdienst</h4>
                  <p className="text-gray-600 text-sm">Bei Stromausfall und elektrischen Notfällen</p>
                  <span className="text-[#0066cc] text-sm font-semibold mt-1 inline-block">Mehr erfahren →</span>
                </div>
              </Link>
              <Link to={`/einsatzgebiete/${city.slug}/sanitaer-heizung`} className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200">
                <Droplet className="w-6 h-6 text-[#0066cc] flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Sanitär & Heizung</h4>
                  <p className="text-gray-600 text-sm">Wasserschaden und Heizungsausfall</p>
                  <span className="text-[#0066cc] text-sm font-semibold mt-1 inline-block">Mehr erfahren →</span>
                </div>
              </Link>
              <Link to={`/einsatzgebiete/${city.slug}/rohrreinigung`} className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200">
                <Wind className="w-6 h-6 text-[#0066cc] flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Rohrreinigung</h4>
                  <p className="text-gray-600 text-sm">Professionelle Beseitigung von Verstopfungen</p>
                  <span className="text-[#0066cc] text-sm font-semibold mt-1 inline-block">Mehr erfahren →</span>
                </div>
              </Link>
              <Link to={`/einsatzgebiete/${city.slug}/glas-notdienst`} className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200">
                <Square className="w-6 h-6 text-[#0066cc] flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Glas-Notdienst</h4>
                  <p className="text-gray-600 text-sm">Schnelle Notverglasung</p>
                  <span className="text-[#0066cc] text-sm font-semibold mt-1 inline-block">Mehr erfahren →</span>
                </div>
              </Link>
              <Link to={`/einsatzgebiete/${city.slug}/einbruchschutz`} className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200">
                <Shield className="w-6 h-6 text-[#0066cc] flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Einbruchschutz</h4>
                  <p className="text-gray-600 text-sm">Sicherheitsmaßnahmen nach Einbrüchen</p>
                  <span className="text-[#0066cc] text-sm font-semibold mt-1 inline-block">Mehr erfahren →</span>
                </div>
              </Link>
            </div>

            <div className="bg-[#e8f4f8] p-6 rounded-xl mb-8">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                <CheckCircle className="w-5 h-5 text-[#0066cc] mr-2" />
                Antwort-Box: Wichtigste Informationen auf einen Blick
              </h4>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Verfügbarkeit:</strong> 24 Stunden am Tag, 7 Tage die Woche</li>
                <li><strong>Einsatzgebiet:</strong> Gesamtes Stadtgebiet {city.name} und alle Stadtteile</li>
                <li><strong>Reaktionszeit:</strong> Schnellstmöglich, in der Regel 30-60 Minuten</li>
                <li><strong>Kontakt:</strong> Telefon 0231 39083687 | E-Mail info@handwerkernotdienst.nrw</li>
                <li><strong>Qualifikation:</strong> Erfahrene und geprüfte Fachkräfte</li>
              </ul>
            </div>

            <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Stadtteile in {city.name}</h3>
            <p className="text-gray-700 mb-6">
              Wir sind in allen Stadtteilen von {city.name} für Sie im Einsatz. Wählen Sie Ihren Stadtteil für spezifische Informationen:
            </p>
          </div>

          {/* Districts */}
          {city.districts && city.districts.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12" data-testid="districts-grid">
              {city.districts.map((district, index) => (
                <Link
                  key={index}
                  to={`/einsatzgebiete/${city.slug}/${district.slug}`}
                  className="location-card bg-gray-50 p-5 rounded-xl"
                  data-testid={`district-card-${index}`}
                >
                  <h4 className="font-bold text-gray-900 mb-2">{district.name}</h4>
                  <p className="text-gray-600 text-sm mb-2">{district.description}</p>
                  <span className="text-sm text-[#0066cc] font-semibold">Mehr erfahren →</span>
                </Link>
              ))}
            </div>
          )}

          {/* FAQ */}
          <div className="mt-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Häufige Fragen zu unserem Service in {city.name}</h2>
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`faq-${index}`}
                  className="bg-gray-50 rounded-lg px-6 border border-gray-200"
                  data-testid={`city-faq-${index}`}
                >
                  <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-[#0066cc] py-4">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-4">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* CTA */}
          <div className="mt-12 bg-[#0066cc] rounded-2xl p-8 text-white text-center">
            <h3 className="text-3xl font-bold mb-4">Notfall in {city.name}?</h3>
            <p className="text-lg mb-6 opacity-90">Rufen Sie uns jetzt an – wir sind rund um die Uhr für Sie da!</p>
            <a href="tel:+4923139083687" data-testid="city-cta-button">
              <Button size="lg" className="bg-[#e63946] hover:bg-[#d62828] text-white text-xl px-10 py-6 rounded-full font-bold">
                <Phone className="w-6 h-6 mr-2" />
                0231 39083687
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CityPage;